<template>
	<div class="project-widget-container" :tabindex="tabindex" @click="handleClick($event)">
		<div class="project-widget-header">
			<!-- @slot Used to display project widget header -->
			<slot name="project-icon" />
			<div class="project-header-meta">
				<h1 class="fc-dark text-ellipsis">
					{{ title }}
				</h1>
				<h6 v-if="subTitle" class="fw-normal fc-light text-ellipsis">
					{{ subTitle }}
				</h6>
			</div>
			<div class="project-widget-settings-icon">
				<Icon name="settings" type="filled" size="small" @click="settings($event)" />
			</div>
		</div>
		<div class="project-widget-body">
			<!-- @slot Used to display project widget key-value type data -->
			<slot />
		</div>
		<div class="project-widget-footer">
			<div class="project-footer-meta">
				<div class="project-footer-label fc-light">
					<h6 class="fw-normal fc-light upper-case text-ellipsis">
						{{ footerLabel }}
					</h6>
				</div>
				<div class="project-footer-toggle">
					<Icon
						:name="'menu-down'"
						type="filled"
						size="small"
						:rotate="innerFooterExpanded ? 0 : 180"
						@click="toggleFooter($event)"
					/>
				</div>
			</div>
			<div class="project-env-grid" :compact="!innerFooterExpanded">
				<!-- @slot Used to display environments -->
				<slot name="footer" />
			</div>
		</div>
	</div>
</template>
<script>
import { defineComponent } from "vue";
import Icon from "./../Icon/Icon";

export default defineComponent({
	name: "ProjectWidget",
	components: {
		Icon
	},
	props: {
		/**
		 * Title of Project
		 */
		title: {
			type: String,
			required: true
		},
		/**
		 * Sub-Title of Project (Optional)
		 */
		subTitle: {
			type: String,
			default: null
		},
		/**
		 * true |  false
		 */
		showSettingsIcon: {
			type: Boolean,
			default: true
		},
		/**
		 * true | false
		 */
		footerExpanded: {
			type: Boolean,
			default: true
		},
		/**
		 * Project Footer label
		 */
		footerLabel: {
			type: String,
			default: null
		},
		/**
		 * tabindex number
		 *  */
		tabindex: {
			type: Number,
			default: -1
		}
	},
	computed: {
		innerFooterExpanded: {
			// getter
			get() {
				return this.footerExpanded;
			},
			// setter
			set(newValue) {
				/**
				 * Whenever footerExpanded is updated.<br/>
				 * use 'footerExpanded.sync' in parent component to achieve 2 way binding.
				 *
				 * @event update:footerExpanded
				 */
				this.$emit("update:footerExpanded", newValue);
			}
		}
	},
	methods: {
		toggleFooter(e) {
			e.stopPropagation();
			this.innerFooterExpanded = !this.innerFooterExpanded;
			/**
			 * toggleFooter event : Trigerred when footer is toggled.
			 *
			 * @event toggleFooter
			 */
			this.$emit("toggleFooter", this.innerFooterExpanded, e);
		},
		handleClick(e) {
			/**
			 * Click event
			 *
			 * @event click
			 */
			this.$emit("click", e);
		},
		settings(e) {
			e.stopPropagation();
			/**
			 * Click on settings icon
			 *
			 * @event settings
			 */
			this.$emit("settings", e);
		}
	}
});
</script>
<style lang="scss">
$background: var(--dashboard-element-light);
div.project-widget-container {
	display: flex;
	flex-direction: column;
	border-radius: 8px;
	cursor: pointer;
	// height: fit-content;
	position: relative;
	// min-width: 411px;
	// max-width: 450px;
	overflow: hidden;
	background-color: var(--dashboard-element-light) !important;

	> div.project-widget-header {
		padding: 16px;
		display: flex;
		> * + * {
			margin-left: 16px;
		}

		border-bottom: 1px solid var(--dashboard-border-light);

		> div.project-widget-settings-icon {
			margin-left: auto;
		}

		> div.project-header-meta {
			> * {
				white-space: normal;
				word-break: break-all;
			}
		}

		// > .flow-picto-container {
		// 	flex: 1 0 auto;
		// }
	}

	> div.project-widget-body {
		padding: 16px 16px 24px 16px;
		display: flex;

		> * {
			min-width: 150px;
		}

		> * + * {
			margin-left: 16px;
		}

		> div.project-stat,
		> div {
			> * + * {
				margin-top: 4px;
			}
		}

		border-bottom: 1px solid var(--dashboard-border-light);
	}
	> div.project-widget-footer {
		padding: 16px;
		display: flex;
		flex-direction: column;

		> div.project-footer-meta {
			display: flex;
			justify-content: space-between;
			align-items: center;
			// padding-bottom: 16px;

			> div.project-footer-toggle {
				display: inline-flex;
				align-items: center;
			}

			> div.project-footer-label {
				> * {
					white-space: normal;
					word-break: break-all;
				}
			}
		}

		> * + * {
			margin-top: 12px;
		}

		> div.project-env-grid {
			display: grid;
			grid-template-columns: repeat(auto-fill, minmax(130px, 1fr));
			grid-gap: 12px;
			grid-auto-rows: 1fr;
			&[compact] {
				grid-template-columns: repeat(auto-fill, minmax(24px, 1fr));
			}
		}
	}

	// &:active,
	// &:hover:not(:active) {
	// 	&:before {
	// 		content: "";
	// 		position: absolute;
	// 		top: 0;
	// 		right: 0;
	// 		bottom: 0;
	// 		left: 0;
	// 		width: 100%;
	// 		height: 100%;
	// 		background-color: var(--dashboard-font-dark);
	// 		border-radius: 8px;
	// 	}
	// }

	&:active {
		filter: contrast(90%);
	}

	&:hover:not(:active) {
		filter: contrast(95%);
	}

	&:focus {
		box-shadow: inset 0 0 0 2px var(--dashboard-primary);
		outline: none;
	}
}

div.flow-project-widget-grid {
	display: grid;
	grid-gap: 12px;
	grid-template-columns: repeat(auto-fill, 450px);
	align-items: flex-start;

	> div.flow-new-placeholder {
		min-height: 250px;
	}
}
</style>
